/**
 * Constants and configuration objects for ScannerConfigurationForm
 */

/**
 * Mapping of sensitive (secret) fields by authentication method
 * These fields require special handling in edit mode:
 * - Never displayed or prefilled when loading edit view
 * - Shown as empty with placeholder indicating secret exists
 * - Send null if unchanged, send new value if modified
 */

/**
 * Converts 24-hour format (0-23) to 12-hour format with am/pm
 * @param {number} hour24 - Hour in 24-hour format (0-23)
 * @returns {string} Formatted hour in 12-hour format (e.g., "1 am", "12 pm")
 */
const formatHour12 = (hour24) => {
  if (
    typeof hour24 !== 'number' ||
    hour24 < 0 ||
    hour24 > 23 ||
    !Number.isInteger(hour24)
  ) {
    throw new Error('hour24 must be an integer between 0 and 23');
  }

  if (hour24 === 0) return '12 am';
  if (hour24 === 12) return '12 pm';
  if (hour24 < 12) return `${hour24} am`;

  return `${hour24 - 12} pm`;
};

/**
 * Run time options (24-hour format, 0-23)
 * Display shows 12-hour format with AM/PM, but value remains 0-23 for backend
 */
export const RUN_TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  label: formatHour12(i),
  value: `${i}`
}));

/**
 * Interval unit options for custom schedule
 */
export const INTERVAL_UNIT_OPTIONS = [
  { label: 'days', value: 'days' },
  { label: 'weeks', value: 'weeks' },
  { label: 'months', value: 'months' }
];

/**
 * Week days for custom schedule
 */
export const WEEK_DAYS = [
  { label: 'M', value: 'M' },
  { label: 'T', value: 'T' },
  { label: 'W', value: 'W' },
  { label: 'Th', value: 'Th' },
  { label: 'F', value: 'F' },
  { label: 'S', value: 'S' },
  { label: 'Su', value: 'Su' }
];

/**
 * Day name mapping for schedule labels
 */
export const DAY_NAME_MAP = {
  M: 'Monday',
  T: 'Tuesday',
  W: 'Wednesday',
  Th: 'Thursday',
  F: 'Friday',
  S: 'Saturday',
  Su: 'Sunday'
};

/**
 * Calculate default hour value (12 hours from current local time)
 * @returns {string} Hour value as string (0-23)
 */
export const getDefaultHour = () => {
  const now = new Date();
  const currentHour = now.getHours();
  const defaultHour = (currentHour + 12) % 24;

  return String(defaultHour);
};

/**
 * Default form values
 */
export const DEFAULT_FORM_VALUES = {
  runFrequency: { unit: 'day', interval: 1 },
  at: getDefaultHour(),
  syncReview: 'ask-for-review',
  emailNotificationsEnabled: false,
  recipientEmails: []
};

/**
 * Default custom schedule state
 */
export const DEFAULT_CUSTOM_SCHEDULE = {
  intervalNumber: 3,
  intervalUnit: 'days',
  repeatDays: [],
  monthDay: 1
};
